import {
  ConnectorCatalogSurfaceSchema,
  ConnectorSurfaceSchema,
  ConnectorSurfaceKindSchema,
} from "@/schema";
import type {
  IntegrationAccess,
  ConnectorCatalogSurface,
  ConnectorSurface,
  ConnectorSurfaceKind,
  ScopeCategory,
  IntegrationServerInstance,
  IntegrationServer,
} from "@/schema";
import fetchClient from "./axiosInstance";
import { activeContextStore } from "./ActiveContextStore";

export interface UploadAttachmentResponse {
  attachmentId: string;
  bucketId: string;
  bucketName: string;
  fileId: string;
  status: string;
  message?: string;
  preview?: Record<string, unknown>;
}

// Outlet connector hierarchy (kinds PROJECT the shared @causit/integration-schema
// declared sets — no local vocabulary):
//   servers   = shared integration definitions
//   instances = org/human scoped authenticated connections
//   surfaces  = instance child rows: surface | channel | tool | file | command
export type ListActor = "outlet" | "human" | "org" | "integration" | ConnectorSurfaceKind;
export type ListIntegration = ScopeCategory;
export type ListTarget = ConnectorSurfaceKind;
export interface ListParams {
  actor: ListActor;
  integration?: ListIntegration; // narrow integrations to a category
  target?: ListTarget; // the resources an integration exposes
  instanceId?: string; // scope resources to one instance
  available?: boolean; // catalog (addable) vs connected
}

// the declared connector kind set (shared schema) — arrives by construction.
const SURFACE_ACTORS = new Set<string>(ConnectorSurfaceKindSchema.options as string[]);
const UPLOAD_CHUNK_BYTES = 128 * 1024;

// mirror of the outlet's safeMachineToken/connectorCatalogMachineId
// (mcp-outlet/src/js/auth/surfaces.ts) — the catalog machine id is the
// boundary identity and must be IDENTICAL on both sides.
const safeMachineToken = (value: unknown, fallback = "surface"): string => {
  const token = String(value ?? "").trim().replace(/[^A-Za-z0-9_.-]+/g, "_").replace(/^_+|_+$/g, "");
  return token.slice(0, 120) || fallback;
};
export const connectorCatalogMachineId = (instanceId: string): string =>
  `connector_catalog_${safeMachineToken(instanceId)}`;

class IntegrationService {
  private readonly baseUrl = import.meta.env.VITE_MCP_OUTLET;
  // User-side MCP calls go through the outlet; the client never calls adapter
  // servers directly. The call rides fetchClient so the ONE auth interceptor
  // (useAuth) refreshes an expired/missing bearer before attaching it — a raw
  // fetch with a stale in-memory token 401s ("Authorization required").
  private async callOutletMcp<T>(
    name: string,
    args: Record<string, unknown>
  ): Promise<T> {
    const response = await fetchClient.post(`${this.baseUrl}/mcp`, {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: { name, arguments: args },
    });

    const body = response.data;
    if (body?.error) {
      throw new Error(body?.error?.message || `${name} failed`);
    }

    return (body?.result?.structuredContent ?? body?.result) as T;
  }

  /**
   * List connector definitions or authenticated connector instances.
   */
  async list(params: ListParams): Promise<{ items: unknown[] }> {
    const { actor, integration, available, instanceId } = params;
    if (actor === "integration") {
      if (available) return { items: await this.getServers() };
      const instances = await this.getInstances(true);
      const items = integration
        ? instances.filter((i) => i.type === integration)
        : instances;
      return { items };
    }
    if (SURFACE_ACTORS.has(actor)) {
      // THE LIST IS WORLD DATA: surface asks read the world-held catalog of
      // one instance (there is no live provider list at the outlet anymore).
      if (!instanceId) {
        throw new Error(
          "surface list requires an instanceId — the surface list is world data (the connector catalog)",
        );
      }
      const surfaces = await this.getSurfaces(instanceId);
      return { items: surfaces.filter((surface) => surface.kind === actor) };
    }
    if (actor === "human" || actor === "org") {
      const response = await fetchClient.post(`${this.baseUrl}/list`, {
        actor,
      });
      return { items: response.data?.items || [] };
    }
    return { items: [] };
  }

  private async blobAsBase64(blob: Blob): Promise<string> {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  }

  private async uploadFileInParts<T>(
    file: File,
    args: {
      sync: { io: "out"; machine: string; total: number };
      batch: { serial: number; status: "complete" };
      metadata: Record<string, unknown>;
    }
  ): Promise<T> {
    const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_BYTES));
    const partId = `${args.sync.machine}:${file.name}:${file.size}:${file.lastModified}:${args.batch.serial}`;
    const uploadTimestamp = new Date().toISOString();
    let latest: T | undefined;
    for (let part = 0; part < totalParts; part += 1) {
      const start = part * UPLOAD_CHUNK_BYTES;
      const chunk = file.slice(start, Math.min(file.size, start + UPLOAD_CHUNK_BYTES));
      latest = await this.callOutletMcp<T>("upload", {
        fileName: file.name,
        content: await this.blobAsBase64(chunk),
        contentType: file.type || "text/csv",
        timestamp: uploadTimestamp,
        sync: args.sync,
        batch: args.batch,
        metadata: {
          ...args.metadata,
          ...(totalParts > 1 ? {
            uploadPart: {
              id: partId,
              serial: part + 1,
              total: totalParts,
              fileName: file.name,
              contentType: file.type || "text/csv",
            },
          } : {}),
        },
      });
    }
    if (!latest) throw new Error("upload produced no response");
    return latest;
  }

  /**
   * Get all integration instances from MCP Outlet
   * @param includeAuth - Whether to include auth information
   */
  async getInstances(includeAuth: boolean = false, includeFiles: boolean = false, active: boolean = false): Promise<IntegrationServerInstance[]> {
    try {
      const response = await fetchClient.post(
        `${this.baseUrl}/auth/getInstances`,
        {
          includeAuth,
          includeFiles,
          active,
        }
      );

      const instances: IntegrationServerInstance[] = response.data.instances || [];


      return instances;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all available integration servers from MCP Outlet
   */
  async getServers(): Promise<IntegrationServer[]> {
    try {
      const response = await fetchClient.post(
        `${this.baseUrl}/auth/getServers`
      );

      const servers: IntegrationServer[] = response.data.servers || [];


      return servers;
    } catch (error) {
      throw error;
    }
  }

  // THE LIST IS WORLD DATA (the surface cut): the instance's surface catalog
  // is a world-held announcement synced by the outlet at registration; the
  // picker reads it through the normal /mcp download door (the hydration
  // point) and ws folds refresh it. The catalog is COMPLETE — search is a
  // local filter, never a provider round trip. The catalog announce is async
  // (world sync), so a fresh registration may need a bounded wait for the
  // world row to land — poll the download, fail loud when it never arrives.
  async getSurfaces(
    instanceId: string,
    opts: { waitMs?: number } = {},
  ): Promise<ConnectorCatalogSurface[]> {
    if (!instanceId) {
      throw new Error("getSurfaces requires an instanceId — the surface list is the instance's world catalog");
    }
    const orgId = activeContextStore.selectedOrgId();
    if (!orgId) throw new Error("getSurfaces requires an active organization");
    const machine = connectorCatalogMachineId(instanceId);
    const deadline = Date.now() + Math.max(0, opts.waitMs ?? 60_000);
    let lastError: unknown;
    do {
      try {
        const response = await this.callOutletMcp<{ artifact?: { input?: { catalog?: { surfaces?: unknown[] } } } }>(
          "download",
          { node: `sci::org:${orgId}`, machine },
        );
        const surfaces = response?.artifact?.input?.catalog?.surfaces;
        if (Array.isArray(surfaces)) {
          return surfaces.map((surface) => ConnectorCatalogSurfaceSchema.parse(surface));
        }
        lastError = new Error(`connector catalog ${machine} carries no surfaces lane`);
      } catch (error) {
        lastError = error;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } while (Date.now() < deadline);
    throw new Error(
      `connector surface catalog ${machine} did not land in world: ${String((lastError as Error)?.message || lastError)}`,
    );
  }

  async registerSurfaces(params: {
    instanceId: string;
    surfaceIds: string[];
    conversationId?: string;
  }): Promise<{ surfaces: ConnectorSurface[]; announce?: { status: string; conversationId?: string } }> {
    const response = await fetchClient.post(`${this.baseUrl}/workflow/surfaces/register`, params);
    const surfaces = response.data?.surfaces || [];
    return {
      surfaces: surfaces.map((surface: unknown) => ConnectorSurfaceSchema.parse(surface)),
      announce: response.data?.announce,
    };
  }

  /**
   * Upload a file through the outlet file adapter.
   */
  async uploadAttachment(
    file: File,
    metadata: {
      bucketId?: string;
      bucketName?: string;
      providedTimestamp?: string;
    } = {}
  ): Promise<UploadAttachmentResponse> {
    try {
      const data = await this.uploadFileInParts<{ fileId?: string; key?: string; url?: string }>(file, {
        sync: { io: "out", machine: `sync:upload:${Date.now().toString(36)}:${file.name}`, total: 1 },
        batch: { serial: 1, status: "complete" },
        metadata: {
          ...(metadata.bucketId ? { bucketId: metadata.bucketId } : {}),
          ...(metadata.bucketName ? { bucketName: metadata.bucketName } : {}),
          ...(metadata.providedTimestamp ? { providedTimestamp: metadata.providedTimestamp } : {}),
        },
      });

      const bucketName = metadata.bucketName || file.name.replace(/\.[^/.]+$/, "");
      const fileId = data.fileId || data.key || "";
      const bucketId = metadata.bucketId || bucketName;


      return {
        attachmentId: fileId,
        bucketId,
        bucketName,
        fileId,
        status: "ready",
        message: "File uploaded successfully",
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Connect to an integration server
   */
  async connectToServer(
    serverName: string,
    additional: any,
    callbackUrl: string,
    access: IntegrationAccess,
    requiresIpWhitelist: boolean = false
  ): Promise<{ url?: string }> {
    try {
      const response = await fetchClient.post(
        `${this.baseUrl}/auth/connect`,
        {
          serverName,
          additional: additional || {},
          callbackUrl,
          access,
          requiresIpWhitelist,
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Submit custom integration
   */
  async submitCustomIntegration(
    serverName: string,
    values: Record<string, string>,
    instanceId: string | undefined,
    instanceLabel: string | undefined,
    requiresIpWhitelist: boolean,
    additional: Record<string, any> | undefined,
    access: IntegrationAccess
  ): Promise<any> {
    try {
      const response = await fetchClient.post(
        `${this.baseUrl}/auth/submitCustom`,
        {
          serverName,
          values,
          instanceId,
          instanceLabel,
          requiresIpWhitelist,
          ...(additional ? { additional } : {}),
          access,
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Disconnect an integration instance
   */
  async disconnectInstance(instanceId: string, isCustomAuth: boolean): Promise<any> {
    try {
      const endpoint = isCustomAuth ? "/auth/removeCustom" : "/auth/disconnect";
      const response = await fetchClient.post(
        `${this.baseUrl}${endpoint}`,
        {
          instanceId,
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const integrationService = new IntegrationService();
export default integrationService;
